import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../../utils/api.js'
import { useLanguage } from '../../i18n/index.jsx'
import useAuthStore from '../../store/authStore.js'

interface FeedbackType {
  value: string
  labelKey: string
  icon: string
}

const TYPES: FeedbackType[] = [
  { value: 'bug',     labelKey: 'typeBug',     icon: '🐛' },
  { value: 'feature', labelKey: 'typeFeature', icon: '💡' },
  { value: 'support', labelKey: 'typeSupport', icon: '🎫' },
  { value: 'other',   labelKey: 'typeOther',   icon: '💬' },
]

interface FeedbackButtonProps {
  triggerClassName?: string
  triggerStyle?: React.CSSProperties
}

export default function FeedbackButton({ triggerClassName = 'floating-action-btn', triggerStyle }: FeedbackButtonProps) {
  const { t } = useLanguage()
  const { user } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('bug')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const backdropMouseDown = useRef(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current) }, [])

  useEffect(() => {
    if (!open) return undefined
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  if (!user) return null

  const isSupport = type === 'support'

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!description.trim()) return
    if (isSupport && !subject.trim()) return
    setSending(true)
    setError('')
    const CATEGORY_MAP: Record<string, string> = { bug: 'bug', feature: 'feature', support: 'general', other: 'general' }
    const autoSubject = isSupport ? subject.trim() : `[${type}] ${location.pathname}`
    try {
      await api.post('/support', {
        subject: autoSubject,
        body: description.trim(),
        category: CATEGORY_MAP[type] || 'general',
        source: 'feedback_widget',
      })
      setSent(true)
      closeTimerRef.current = setTimeout(() => { setOpen(false); setSent(false); setDescription(''); setSubject(''); setType('bug'); setError('') }, 2500)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr.response?.data?.error || t('somethingWentWrong'))
      setSending(false)
    }
  }

  return (
    <>
      {/* Trigger button */}
      <button
        className={triggerClassName}
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          height: 36, padding: '0 14px', borderRadius: 'var(--radius-lg)',
          background: 'var(--bg-surface)', color: 'var(--text-muted)',
          border: '1px solid var(--border-default)',
          fontSize: 'var(--text-sm)', fontWeight: 500, cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          transition: 'color 0.15s, border-color 0.15s',
          pointerEvents: 'auto',
          ...triggerStyle,
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--accent)' }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-default)' }}
        title={t('reportBug')}
      >
        🐛 {t('feedback')}
      </button>

      {/* Modal — portaled to body to escape pointerEvents:none FAB container */}
      {open && createPortal(
        <div
          role="presentation"
          style={{
            position: 'fixed', inset: 0, zIndex: 'var(--z-modal, 1000)',
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => { backdropMouseDown.current = e.target === e.currentTarget }}
          onMouseUp={(e: React.MouseEvent<HTMLDivElement>) => { if (backdropMouseDown.current && e.target === e.currentTarget) setOpen(false); backdropMouseDown.current = false }}
        >
          <div
            style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-xl)', padding: 28,
              width: '100%', maxWidth: 440,
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            {sent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 'var(--text-display-lg)', marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 4 }}>
                  {isSupport ? (t('ticketCreated')) : (t('feedbackSent'))}
                </div>
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: isSupport ? 12 : 0 }}>
                  {isSupport ? (t('ticketCreatedDesc')) : (t('feedbackSentDesc'))}
                </div>
                {isSupport && (
                  <button
                    onClick={() => { setOpen(false); setSent(false); navigate('/support') }}
                    style={{
                      padding: '6px 16px', borderRadius: 'var(--radius)', border: 'none',
                      background: 'var(--accent)', color: 'var(--text-inverse)',
                      fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
                    }}
                  >{t('viewTickets')}</button>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 700 }}>{t('sendFeedback')}</h3>
                  <button
                    onClick={() => setOpen(false)}
                    aria-label={t('close')}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--text-xl)', padding: '2px 6px' }}
                  >✕</button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {error && (
                    <div style={{
                      background: 'var(--danger-dim)', border: '1px solid rgba(248,113,113,0.2)',
                      color: 'var(--danger)', padding: '8px 12px', borderRadius: 'var(--radius)',
                      fontSize: 'var(--text-caption)',
                    }}>
                      {error}
                    </div>
                  )}
                  {/* Type selector */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    {TYPES.map(ft => (
                      <button
                        key={ft.value} type="button"
                        onClick={() => setType(ft.value)}
                        style={{
                          flex: 1, padding: '8px 4px', borderRadius: 'var(--radius)',
                          border: `1px solid ${type === ft.value ? 'var(--accent)' : 'var(--border-default)'}`,
                          background: type === ft.value ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                          color: type === ft.value ? 'var(--accent)' : 'var(--text-secondary)',
                          cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 500,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        <span style={{ fontSize: 'var(--text-xl)' }}>{ft.icon}</span>
                        {t(ft.labelKey)}
                      </button>
                    ))}
                  </div>

                  {/* Page indicator */}
                  <div style={{
                    fontSize: 'var(--text-sm)', color: 'var(--text-muted)',
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius)',
                    padding: '6px 10px',
                  }}>
                    Page: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>{location.pathname}</span>
                  </div>

                  {/* Subject (support only) */}
                  {isSupport && (
                    <input
                      type="text"
                      value={subject}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                      placeholder={t('ticketSubjectPlaceholder')}
                      required
                      style={{
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                        borderRadius: 'var(--radius)', padding: '8px 12px',
                        fontSize: 'var(--text-base)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box',
                      }}
                    />
                  )}

                  {/* Description */}
                  <textarea
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    placeholder={t('feedbackPlaceholder')}
                    required
                    rows={5}
                    autoFocus
                    style={{
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius)', padding: '10px 14px',
                      fontSize: 'var(--text-base)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)',
                      resize: 'vertical',
                    }}
                  />

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setOpen(false)}
                      style={{
                        padding: '8px 16px', borderRadius: 'var(--radius)', border: 'none',
                        background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--text-caption)',
                      }}>
                      {t('cancel')}
                    </button>
                    <button type="submit" disabled={sending || !description.trim() || (isSupport && !subject.trim())}
                      style={{
                        padding: '8px 20px', borderRadius: 'var(--radius)', border: 'none',
                        background: 'var(--accent)', color: 'var(--text-inverse)',
                        cursor: sending ? 'wait' : 'pointer', fontSize: 'var(--text-caption)', fontWeight: 600,
                        opacity: sending || !description.trim() || (isSupport && !subject.trim()) ? 0.6 : 1,
                      }}>
                      {sending ? (t('sending')) : isSupport ? (t('submitTicket')) : (t('send'))}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
